import React, { useEffect, useReducer, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { CircleCheck } from "lucide-react";
// @ts-ignore — legacy JS modules without types
import Layout from "../../Layout";
// @ts-ignore
import { init as initRaw, patientReducer as patientReducerRaw } from "../../../additional_files/reducer";
// @ts-ignore
import { verifyUser } from "../../../authRequests";
import { useSessionBootstrap } from "../../Account/Billing/hooks";
import { ApiAuth, ApiError } from "../../../lib/api";
import { Button } from "../../shadcn/button";
import { readNightRefusal } from "./api";
import { NightForm, blockOfField, useNightAccordion } from "./NightForm";
import { NightPicker } from "./NightPicker";
import { NightSummary, SaveControl, SummaryStatus } from "./NightSummary";
import { NightDraft, addDays, nightTitle, previewMetrics, toDraft, toNightInput, weekDays, weekRange } from "./nightClock";
import { useSaveNight, useSleepNight, useSleepWeek } from "./useSleepDiary";

type DashAction = { type: string; payload?: any; [key: string]: any };
const patientReducer = patientReducerRaw as React.Reducer<any, DashAction>;
const init = initRaw as (state: any) => any;

const WAKE_BLOCK = 2;
const TOAST_MS = 2600;
const GRID = "grid items-start gap-6 min-[1100px]:grid-cols-[minmax(0,1fr)_320px]";
const RAIL = "order-first min-[1100px]:order-none min-[1100px]:sticky min-[1100px]:top-6";

export default function SleepDiaryPage() {
  const { state } = useLocation() as { state: any };
  const [dashState, dispatch] = useReducer(patientReducer, state, init);
  const loadedRef = useRef<HTMLDivElement | null>(null);
  const hydratedRef = useRef(false);

  useEffect(() => {
    verifyUser(state ? state.jwt : "", loadedRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const needsHydration = !dashState?.patient?.pid;
  const bootstrap = useSessionBootstrap({ jwt: null }, needsHydration);

  useEffect(() => {
    if (hydratedRef.current) return;
    if (!bootstrap.data) return;
    hydratedRef.current = true;
    // The legacy patientReducer assumes every collection field is an array;
    // bootstrap returns only part of the session, so the rest is defaults-filled
    // as Account.tsx does.
    dispatch({
      type: "UPDATE_STATE",
      payload: {
        ...dashState,
        db: bootstrap.data.db,
        patient: bootstrap.data.patient,
        organization: bootstrap.data.organization,
        locations: bootstrap.data.locations,
        selectedLocation: bootstrap.data.selectedLocation,
        firstLoading: false,
        userType: bootstrap.data.userType,
        guardianOf: bootstrap.data.dependents || [],
        appointments: dashState?.appointments || [],
        doctors: dashState?.doctors || [],
        notifications: dashState?.notifications || [],
        services: dashState?.services || [],
        serviceCriteria: dashState?.serviceCriteria || [],
        insurers: dashState?.insurers || [],
        relationship: dashState?.relationship || null,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootstrap.data]);

  const auth = { jwt: dashState?.jwt || null };

  return (
    <Layout state={{ ...dashState }} dispatch={dispatch}>
      <div ref={loadedRef} style={{ opacity: 0 }} className="relative flex flex-1 w-full h-full flex-col">
        <SleepDiary auth={auth} />
      </div>
    </Layout>
  );
}

/** Neutral blocks where the form and the rail will be; no spinner. */
function Placeholders() {
  return (
    <div className={GRID} aria-busy>
      <div className="h-[420px] rounded-[12px] bg-neutral-100" />
      <div className={`${RAIL} h-[360px] rounded-[12px] bg-neutral-100`} />
    </div>
  );
}

function LoadError({ onRetry }: { onRetry(): void }) {
  return (
    <div className="flex flex-wrap items-center gap-3 text-neutral-500">
      We could not load this night.
      <Button type="button" variant="outline" size="sm" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

/** Last night is the server's (D20): the page opens on the week it answers with no range. */
function SleepDiary({ auth }: { auth: ApiAuth }) {
  // null = the week that ends on last night; an earlier page is named by its last night.
  const [weekEnd, setWeekEnd] = useState<string | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const [toast, setToast] = useState(0);

  const lastWeek = useSleepWeek(auth);
  // On the latest page this is the same query as lastWeek: one request.
  const pageWeek = useSleepWeek(auth, weekEnd ? weekRange(weekEnd) : undefined);
  const lastNight = lastWeek.data?.lastNight;

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(0), TOAST_MS);
    return () => clearTimeout(t);
  }, [toast]);

  // The night opened first is pinned: a week refetch after the clinic's midnight
  // must not swap the night under unsaved edits (wave-3 review m2).
  useEffect(() => {
    if (day === null && lastNight) setDay(lastNight);
  }, [day, lastNight]);

  const pickerEnd = weekEnd ?? lastNight;
  const selected = day ?? lastNight;
  const lastSaved = new Set(lastWeek.data?.days ?? []);

  let body: React.ReactNode;
  if (lastWeek.error) {
    const err = lastWeek.error;
    body =
      err instanceof ApiError && err.status === 409 && err.code === "TIME_ZONE_MISSING" ? (
        <p className="text-neutral-500">Your clinic has not set its time zone yet.</p>
      ) : (
        <LoadError onRetry={() => lastWeek.refetch()} />
      );
  } else if (!lastNight || !selected) {
    body = <Placeholders />;
  } else {
    body = (
      <NightEditor
        key={selected}
        auth={auth}
        day={selected}
        lastNight={lastNight}
        last7={weekDays(lastNight).map((d) => lastSaved.has(d))}
        onSaved={() => setToast((n) => n + 1)}
      />
    );
  }

  return (
    <div className="mx-auto w-full max-w-[1040px] px-4 pb-24 pt-4 text-neutral-950 min-[860px]:px-8 min-[860px]:pt-8 min-[1100px]:pb-16">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-6">
        <div>
          <h1 className="text-2xl font-semibold leading-8 tracking-[-0.02em]">Sleep Diary</h1>
          <p className="mt-1 text-neutral-500">A minute each morning about the night you just had.</p>
        </div>
        {!lastWeek.error && (
          <div className="max-[859px]:w-full">
            <NightPicker
              loading={!pickerEnd || !lastNight}
              days={pickerEnd ? weekDays(pickerEnd) : []}
              selected={selected ?? ""}
              savedDays={new Set(pageWeek.data?.days ?? [])}
              onSelect={setDay}
              onPrev={() => pickerEnd && setWeekEnd(addDays(weekRange(pickerEnd).from, -1))}
              onNext={() => {
                if (!pickerEnd || !lastNight) return;
                const next = addDays(pickerEnd, 7);
                setWeekEnd(next >= lastNight ? null : next);
              }}
              nextDisabled={!lastNight || !pickerEnd || pickerEnd >= lastNight}
            />
          </div>
        )}
      </div>
      {pageWeek.error && !lastWeek.error && (
        <p role="alert" className="mb-4 text-[13px] text-neutral-500">
          We could not load which of these nights are saved.{" "}
          <button type="button" className="font-medium text-brand hover:underline" onClick={() => pageWeek.refetch()}>
            Try again
          </button>
        </p>
      )}
      {body}
      {toast > 0 && (
        <div
          key={toast}
          role="status"
          className="fixed bottom-24 right-6 z-30 flex max-w-[340px] items-start gap-2.5 rounded-[10px] border border-neutral-200 bg-white px-4 py-3 shadow-toast animate-toast-in motion-reduce:animate-fade-in min-[1100px]:bottom-6"
        >
          <CircleCheck size={16} className="mt-0.5 shrink-0 text-green-600" aria-hidden />
          <div>
            <div className="font-medium">Night saved</div>
            <div className="text-[13px] text-neutral-500">Your care team can now see it.</div>
          </div>
        </div>
      )}
    </div>
  );
}

type NightEditorProps = {
  auth: ApiAuth;
  day: string;
  lastNight: string;
  last7: boolean[];
  onSaved(): void;
};

/** One night: form and rail. Keyed by day, so switching night discards unsaved edits. */
function NightEditor({ auth, day, lastNight, last7, onSaved }: NightEditorProps) {
  const night = useSleepNight(auth, day);
  const save = useSaveNight(auth);
  const accordion = useNightAccordion(false);
  const [draft, setDraft] = useState<NightDraft | null>(null);
  const [dirty, setDirty] = useState(false);
  const [fieldError, setFieldError] = useState<{ field: string; reason: string } | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  // Set before mutate, cleared on settle: a second click before the re-render is a no-op.
  const savingRef = useRef(false);
  // After a conflict: Save stays off while the night reloads, and when the reload fails.
  const [reload, setReload] = useState<"idle" | "loading" | "failed">("idle");

  const { reset } = accordion;
  useEffect(() => {
    if (!night.data) return;
    setDraft(toDraft(day, night.data));
    setDirty(false);
    setFieldError(null);
    reset(!!night.data.night);
  }, [day, night.data, reset]);

  if (night.error && !night.data) return <LoadError onRetry={() => night.refetch()} />;
  if (!draft || !night.data) return <Placeholders />;

  const stored = night.data.night;
  const preview = previewMetrics(draft);
  const status: SummaryStatus = dirty ? "dirty" : stored ? "saved" : "new";
  const saving = save.isPending;
  const canSave = draft.awakeningCount !== null && preview.ok && status !== "saved" && !saving && reload === "idle";
  const statusText = !preview.ok
    ? "Fix the highlighted time to save."
    : draft.awakeningCount === null
      ? "Answer “During the night” to save."
      : status === "new"
        ? "Same as usual? Save it as is."
        : status === "dirty"
          ? "You have unsaved changes."
          : "Saved. Edit anything and save again.";
  // D13: the stored value wins once saved; before that, the preview of the draft, once "Waking up" is answered.
  const metrics = status === "saved" && stored ? stored.metrics : preview.ok && accordion.done.has(WAKE_BLOCK) ? preview.metrics : null;

  const onChange = (next: NightDraft) => {
    setDraft(next);
    setDirty(true);
    setFieldError(null);
    setPageError(null);
  };

  const reloadNight = () => {
    setReload("loading");
    setPageError("This night was changed somewhere else. Reloading it.");
    night.refetch().then((r) => {
      if (r.isError || !r.data) {
        setReload("failed");
        setPageError(null);
        return;
      }
      setDraft(toDraft(day, r.data));
      setDirty(false);
      setFieldError(null);
      reset(!!r.data.night);
      setReload("idle");
      setPageError("This night was changed somewhere else, so it was reloaded. Check it and save again if needed.");
    });
  };

  const onSave = () => {
    if (!canSave || savingRef.current) return;
    savingRef.current = true;
    setPageError(null);
    save.mutate(toNightInput(draft), {
      onSuccess: (res) => {
        setDraft(toDraft(day, { success: true, night: res.night, prefill: null }));
        setDirty(false);
        setFieldError(null);
        reset(true);
        onSaved();
      },
      onSettled: () => {
        savingRef.current = false;
      },
      onError: (err) => {
        if (err instanceof ApiError && err.status === 409 && err.code === "SLEEP_NIGHT_CONFLICT") {
          // Another save landed first: load it and drop the draft, so a retry cannot overwrite it.
          reloadNight();
          return;
        }
        const refusal = readNightRefusal(err);
        const block = refusal ? blockOfField(refusal.field) : -1;
        if (refusal && block >= 0) {
          setFieldError(refusal);
          accordion.open(block);
          return;
        }
        setPageError(
          err instanceof ApiError && err.status === 400 ? err.message : "We could not save this night. Try again."
        );
      },
    });
  };

  const saveProps = { canSave, saving, statusText, onSave };

  return (
    <>
      <div className={GRID}>
        <div>
          {pageError && <p className="mb-3 text-[13px] text-red-600">{pageError}</p>}
          {reload === "failed" && (
            <p className="mb-3 text-[13px] text-red-600">
              This night changed somewhere else and we could not reload it.{" "}
              <button type="button" className="font-medium underline" onClick={reloadNight}>
                Try again
              </button>
            </p>
          )}
          {stored?.schemaVersion === 1 && (
            <p className="mb-3 text-[13px] text-neutral-500">From your earlier diary. Check the times before you save.</p>
          )}
          <div className="rounded-[12px] border border-neutral-200 bg-white">
            <NightForm
              draft={draft}
              onChange={onChange}
              accordion={accordion}
              day={day}
              isLastNight={day === lastNight}
              fieldError={fieldError}
            />
          </div>
        </div>
        <aside className={RAIL}>
          <NightSummary
            title={nightTitle(day)}
            status={status}
            metrics={metrics}
            bar={{
              preview,
              awakeningCount: draft.awakeningCount,
              awakeningMinutes: draft.awakeningMinutes,
              awakeningTimes: draft.awakeningTimes,
            }}
            done={accordion.done}
            last7={last7}
            {...saveProps}
          />
        </aside>
      </div>
      <div className="fixed inset-x-0 bottom-0 z-20 border-t border-neutral-200 bg-white/95 px-4 py-3 backdrop-blur min-[1100px]:hidden">
        <SaveControl {...saveProps} />
      </div>
    </>
  );
}
